import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Monitor, LogOut, ShieldCheck, Clock, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-night border border-line px-4 py-3 text-sm text-ink focus:border-brand outline-none font-mono transition-colors";
const labelClass = "block font-mono text-xs uppercase text-mute mb-2";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const VendorDesk = () => {
  const [user, setUser] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [notices, setNotices] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadDesk = useCallback(async () => {
    try {
      const [meRes, noticesRes] = await Promise.all([
        axios.get(`${API}/vendor/me`, { withCredentials: true }),
        axios.get(`${API}/vendor/notices`, { withCredentials: true }),
      ]);
      setProfile(meRes.data.profile);
      setNotices(noticesRes.data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data.role !== "vendor") throw new Error("not vendor");
        setUser(res.data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
          if (res.data.role !== "vendor") throw new Error("not vendor");
          setUser(res.data);
        } catch {
          setUser(null);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (user) loadDesk();
  }, [user, loadDesk]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.role !== "vendor") {
        setError("This desk is for registered vendors. Admins use /admin.");
        return;
      }
      setUser(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Cannot reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setProfile(null);
      setNotices([]);
    }
  };

  const status = profile?.status || "pending";
  const StatusIcon = status === "approved" ? ShieldCheck : status === "rejected" ? XCircle : Clock;

  return (
    <div className="bg-night text-ink font-sans antialiased min-h-screen selection:bg-brand selection:text-night">
      <header className="bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="vendor-brand-link">
            <Monitor className="w-6 h-6 text-brand" strokeWidth={2} />
            <span className="font-mono text-sm tracking-wider uppercase font-bold text-brand">
              DSS Tender Hub — Vendor Desk
            </span>
          </a>
          {user ? (
            <button onClick={handleLogout} className="flex items-center gap-2 border border-line hover:border-brand text-mute hover:text-brand font-mono text-xs uppercase tracking-wider px-4 py-2 transition-colors" data-testid="vendor-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          ) : (
            <a href="/register" className="font-mono text-xs uppercase text-mute hover:text-brand transition-colors" data-testid="vendor-register-link">
              New Vendor? Register →
            </a>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        {user === undefined && (
          <p className="font-mono text-xs uppercase text-mute tracking-widest" data-testid="vendor-loading">Checking session...</p>
        )}

        {user === null && (
          <div className="max-w-md mx-auto">
            <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">Vendor Access</p>
            <h1 className="font-display text-4xl uppercase mb-8 tracking-wide">Vendor Login</h1>
            <form onSubmit={handleLogin} className="bg-surface border border-line p-8 space-y-6" data-testid="vendor-login-form">
              <div>
                <label className={labelClass} htmlFor="vd-email">Email</label>
                <input type="email" id="vd-email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="registered email" data-testid="vendor-email-input" />
              </div>
              <div>
                <label className={labelClass} htmlFor="vd-password">Password</label>
                <input type="password" id="vd-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="password" data-testid="vendor-password-input" />
              </div>
              {error && <p className="font-mono text-xs text-warn" data-testid="vendor-login-error">{error}</p>}
              <button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brandhover disabled:opacity-60 text-night font-mono text-xs font-bold uppercase tracking-widest py-4 transition-colors" data-testid="vendor-login-submit-btn">
                {loading ? "Verifying..." : "Sign In →"}
              </button>
              <p className="font-mono text-[11px] text-mute text-center">
                No account? <a href="/register" className="text-brand hover:underline">Register with Aadhaar verification</a>
              </p>
            </form>
          </div>
        )}

        {user && (
          <div data-testid="vendor-dashboard">
            <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">Vendor Desk</p>
            <h1 className="font-display text-[clamp(28px,3.5vw,48px)] uppercase mb-8 tracking-wide">
              Welcome, {user.name}
            </h1>

            <div
              className={`border p-6 mb-10 flex items-start gap-4 ${
                status === "approved" ? "border-ok bg-surface" : status === "rejected" ? "border-warn bg-surface" : "border-line bg-surface"
              }`}
              data-testid="vendor-status-banner"
            >
              <StatusIcon className={`w-6 h-6 shrink-0 ${status === "approved" ? "text-ok" : "text-warn"}`} strokeWidth={1.5} />
              <div>
                <p className="font-mono text-xs uppercase tracking-widest font-bold mb-1">
                  Verification: {status}
                </p>
                <p className="text-sm text-mute">
                  {status === "approved"
                    ? "Your Aadhaar verification is complete. You can participate in live tenders below."
                    : status === "rejected"
                      ? "Your registration could not be verified. Contact the procurement desk for details."
                      : "Your documents are under review. Verification is completed by a physical/document check — you will be able to participate once approved."}
                </p>
                {profile && (
                  <p className="font-mono text-[11px] text-mute mt-2">
                    Aadhaar on file: {profile.aadhaar_masked} · Property: {profile.property_type}
                  </p>
                )}
              </div>
            </div>

            <h2 className="font-mono text-xs uppercase text-brand tracking-widest mb-4">Live Tender Notices & Security Deposits</h2>
            {notices.length === 0 ? (
              <div className="border border-line p-8 bg-surface text-center" data-testid="vendor-notices-empty">
                <p className="text-sm font-mono text-mute uppercase tracking-wider">No live notices at the moment.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="vendor-notices-list">
                {notices.map((n) => (
                  <div key={n.id} className="bg-surface border border-line p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`vendor-notice-${n.id}`}>
                    <div>
                      <p className="font-mono text-[11px] text-brand uppercase tracking-wider mb-1">{n.district}</p>
                      <h3 className="text-base font-bold uppercase">{n.point}</h3>
                      {n.sqft && <p className="text-xs text-mute mt-1">{n.sqft}</p>}
                      {n.message && <p className="text-xs text-mute mt-1">{n.message}</p>}
                    </div>
                    <div className="md:text-right shrink-0">
                      {n.deposit_amount ? (
                        n.deposit_paid ? (
                          <span className="inline-block font-mono text-xs uppercase tracking-wider text-ok border border-ok px-3 py-2" data-testid={`deposit-paid-${n.id}`}>
                            Deposit Received — {inr(n.deposit_amount)}
                          </span>
                        ) : (
                          <div data-testid={`deposit-due-${n.id}`}>
                            <p className="font-mono text-xs uppercase tracking-wider text-warn mb-1">
                              Security Deposit: {inr(n.deposit_amount)}
                            </p>
                            <p className="font-mono text-[10px] text-mute">
                              Pay at the office / via UPI — our team marks it received here.
                            </p>
                          </div>
                        )
                      ) : (
                        <span className="font-mono text-[11px] text-mute uppercase">No deposit required</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default VendorDesk;
